/**
 * /control/hub — Hub de Vencimientos global.
 * Fase 17 · Libro de Control Fiduciario.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import {
    SEMAFORO, CATEGORIA_LABEL, fmtFecha,
} from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
    RefreshCw, AlertTriangle, ChevronRight,
    FileSignature, Shuffle, FileX2, KeyRound, Gavel, Zap,
} from "lucide-react";

const CAT_ICON = {
    dictamen_f09: AlertTriangle,
    cotizacion: FileSignature,
    poder: KeyRound,
    requerimiento: Gavel,
    convenio: FileSignature,
    sustitucion: Shuffle,
    contingencia: Zap,
};

const CAT_PATH = {
    convenio: "/control/convenios",
    sustitucion: "/control/sustituciones",
    poder: "/control/poderes",
    requerimiento: "/control/requerimientos",
    contingencia: "/control/contingencia",
    dictamen_f09: "/aceptacion/dictamenes",
    cotizacion: "/honorarios/cotizaciones",
};

export default function HubControl() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/control/hub-vencimientos");
            setData(r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    if (loading && !data) return <div className="p-6 text-muted-foreground">Cargando hub…</div>;
    if (!data) return null;

    const cats = Object.entries(data.categorias);
    const totalRojo = cats.reduce((s, [, t]) => s + (t.rojo || 0), 0);
    const totalAmbar = cats.reduce((s, [, t]) => s + (t.ambar || 0), 0);

    return (
        <div className="space-y-6" data-testid="hub-control">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Hub de Vencimientos · Control Fiduciario
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Semáforo global de los módulos M-11 a M-15 + Contingencia · Dirección Fiduciaria.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-900 border-red-300 border" data-testid="hub-badge-rojos">
                        {totalRojo} rojos
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-900 border-amber-300 border" data-testid="hub-badge-ambar">
                        {totalAmbar} ámbar
                    </Badge>
                    <Button size="sm" variant="outline" onClick={load} data-testid="hub-refresh">
                        <RefreshCw className="h-4 w-4 mr-1" /> Refrescar
                    </Button>
                </div>
            </div>

            {/* Tiles por categoría */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {cats.map(([cat, tiles]) => {
                    const Icon = CAT_ICON[cat] || AlertTriangle;
                    const path = CAT_PATH[cat];
                    const peor = tiles.rojo > 0 ? "rojo" : tiles.ambar > 0 ? "ambar" : "verde";
                    return (
                        <Card
                            key={cat}
                            className={`${SEMAFORO[peor]} border-2 cursor-pointer transition-transform hover:scale-[1.02]`}
                            data-testid={`tile-${cat}`}
                        >
                            <Link to={path || "#"}>
                                <CardHeader className="pb-1 flex-row items-center justify-between">
                                    <CardTitle className="text-sm">{CATEGORIA_LABEL[cat]}</CardTitle>
                                    <Icon className="h-4 w-4" />
                                </CardHeader>
                                <CardContent className="pt-1">
                                    <p className="text-3xl font-bold">{tiles.total}</p>
                                    <div className="flex gap-2 text-xs mt-2">
                                        {tiles.rojo > 0 && <span className="font-semibold">● {tiles.rojo} rojo</span>}
                                        {tiles.ambar > 0 && <span>● {tiles.ambar} ámbar</span>}
                                        <span className="opacity-70">● {tiles.verde} verde</span>
                                    </div>
                                </CardContent>
                            </Link>
                        </Card>
                    );
                })}
            </div>

            {/* Top-20 */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Top 20 próximos a vencer · todas las categorías</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <table className="w-full text-sm" data-testid="hub-top20-table">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Semáforo</th>
                                <th className="px-3 py-2 text-left">Categoría</th>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Vence / Edad</th>
                                <th className="px-3 py-2 text-right">Días</th>
                                <th className="px-3 py-2 text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_20.length === 0 && (
                                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                                    Sin items próximos a vencer. Todo en verde.
                                </td></tr>
                            )}
                            {data.top_20.map((item, i) => {
                                const path = CAT_PATH[item.categoria] || "#";
                                const url = item.id ? `${path}/${item.id}` : path;
                                return (
                                    <tr key={i} className="border-t hover:bg-muted/20">
                                        <td className="px-3 py-2">
                                            <Badge className={SEMAFORO[item.semaforo] + " border"}>
                                                {item.semaforo.toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2 text-xs">{CATEGORIA_LABEL[item.categoria]}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{item.folio || "—"}</td>
                                        <td className="px-3 py-2 text-xs">
                                            {item.vence_at
                                                ? fmtFecha(item.vence_at)
                                                : (item.edad_dias ? `${item.edad_dias} d. de antigüedad` : "—")}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">
                                            {item.dias_restantes !== null ? item.dias_restantes : "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Link to={url}>
                                                <ChevronRight className="h-4 w-4 inline" />
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground">
                Generado: {fmtFecha(data.generado_at)} · Verde &gt;15d · Ámbar ≤15d · Rojo ≤3d o vencido.
            </p>
        </div>
    );
}
